import * as React from 'react';
import { useEffect, useState } from 'react';
import { Element as ReactScrollElement } from 'react-scroll';

import { useMutation } from '@apollo/client';
import { copyTest } from '@coursera-web/copy-test';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import { branch, compose, renderComponent, withProps } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';
import connectToRouter from 'js/lib/connectToRouter';
import { getCmsLocaleString } from 'js/lib/language';
import requestCountry from 'js/lib/requestCountry';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { isSuperuser } from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Grid } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import PersonalizedModal from 'bundles/degree-description/components/modules/personalization/PersonalizedModal';
import PersonalizedTooltip from 'bundles/degree-description/components/modules/personalization/PersonalizedTooltip';
import { useDegreeDescriptionPersonalizedData } from 'bundles/degree-description/contexts/DegreeDescriptionPersonalizationContext';
import { DegreeDescriptionEventingContextProvider } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { DegreeDescriptionPagePersonalizationProvider } from 'bundles/degree-description/providers/DegreeDescriptionPagePersonalizationProvider';
import DegreeDescriptionPageThemeProvider from 'bundles/degree-description/providers/DegreeDescriptionPageThemeProvider';
import type { PersonalizedData } from 'bundles/degree-description/types';
import { getCtaActionXddp, getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import type { MessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import adsTracker from 'bundles/event-tracking/lib';
import { ChiliPiperProvider } from 'bundles/expression-of-interest/components/ChiliPiper/ChiliPiperProvider';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import { RETRIEVE_COMPLETED_QUALIFICATION_SURVEY } from 'bundles/expression-of-interest/queries/RetrieveCompletedQualificationSurvey';
import type {
  RetrieveCompletedSurveyResponse,
  RetrieveCompletedSurveyVariables,
} from 'bundles/expression-of-interest/queries/RetrieveCompletedQualificationSurvey';
import { addDriftChatWidget } from 'bundles/expression-of-interest/utils/driftUtils';
import { EOI_SUBMISSION_LOOKBACK_PERIOD } from 'bundles/expression-of-interest/utils/util';
import { AVAILABILITY_STATUS } from 'bundles/premium-hub/types';
import { isFakeDegree } from 'bundles/premium-hub/utils/fakeDegreeUtils';
import { AUTOCOMPLETE_PHOTO_SIZE, DegreeIcon } from 'bundles/search-common/constants/autocomplete';
import { saveRecentlyViewed } from 'bundles/search-common/utils/SearchUtils';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';
import { GetDegreePageContentDocument } from 'bundles/xddp/__generated__/contentfulOperations';
import type {
  AdmissionsContentFragment,
  DegreePageContentFragment,
  GetDegreePageContentQuery,
  GetDegreePageContentQueryVariables,
  NavigationElementFieldsFragment,
} from 'bundles/xddp/__generated__/contentfulTypes';
import Degree404 from 'bundles/xddp/components/Degree404';
import DegreeNotAvailable from 'bundles/xddp/components/DegreeNotAvailable';
import type { GetDegreeBySlug_Dedupe1Query } from 'bundles/xddp/components/__queries__/__generated__/getDegreeBySlug';
import type {
  GetUpsellsBySlugQuery,
  GetUpsellsBySlugQueryVariables,
} from 'bundles/xddp/components/__queries__/__generated__/getUpsellsBySlug';
import getDegreeBySlug from 'bundles/xddp/components/__queries__/getDegreeBySlug.graphql';
import getUpsellsBySlug from 'bundles/xddp/components/__queries__/getUpsellsBySlug.graphql';
import Container from 'bundles/xddp/components/shared/Container';
import SharedPremiumHeader from 'bundles/xddp/components/shared/SharedPremiumHeader';
import { PREMIUM_PAGE_TYPES } from 'bundles/xddp/constants';
import type { UpsellsByDegreeSlug, UserAgentProps } from 'bundles/xddp/types';
import { filterUpsells, transformSlugIfNeeded } from 'bundles/xddp/utils/helpers';
import { setLastSeenDdp } from 'bundles/xddp/utils/lastSeenDdpUtils';

type RouterProps = {
  slug: string;
  isPreview: boolean;
  specializationSlug: string;
  currentRouteName: string;
  pathname: string;
  locale: string;
};

type QueryProps = RouterProps;

type PageContentType = DegreePageContentFragment;

type PropsFromPersonalization = {
  personalizedDdpData?: PersonalizedData;
};

type PropsFromCatalogDegreeQuery = PropsFromPersonalization &
  QueryProps & {
    degreeAvailabilityStatus: string;
    blockedCountries: Array<string>;
    id: string;
    productVariant: string;
  };

type QueryOuterProps = QueryProps & {
  pageContent: PageContentType;
  admissionsRequirements: AdmissionsContentFragment;
};

type PropsToDegreeUpsellsQuery = {
  slug: string;
};

type FromWithProps = {
  currentNavElement?: NavigationElementFieldsFragment | null;
};

type PropsFromDegreeUpsellsQuery = {
  listOfUpsellsLinkedtoThisDegree?: UpsellsByDegreeSlug;
};

type Props = QueryOuterProps &
  UserAgentProps &
  FromWithProps &
  PropsFromCatalogDegreeQuery &
  PropsFromDegreeUpsellsQuery & {
    isSurveyComplete: boolean;
  };

type State = {
  isStickyBarPresent: boolean;
  isStickyEoiPresent: boolean;
  showPersonalizedEoiModal: boolean;
  personalizedEoiModalStep: string;
  shouldShowTooltip: boolean;
  shouldShowModal: boolean;
  messagesToDisplay: MessagesToDisplay;
};

const DEFAULT_DISCLAIMER =
  'Coursera does not grant credit, and does not represent that any institution other than the degree granting institution will recognize the credit or credential awarded by the institution; the decision to grant, accept, or transfer credit is subject to the sole and absolute discretion of an educational institution.';

const getS12nContent = (pageContent: PageContentType, specializationSlug: $TSFixMe) => {
  const { specializationsCollection } = pageContent;
  const specializations = specializationsCollection ? specializationsCollection.items : [];

  if (!specializations.length) {
    return null;
  }

  const s12nContent = specializations.find(
    (specialization) => specialization !== null && specialization.slug === specializationSlug
  );

  if (!s12nContent) {
    return null;
  }
  return s12nContent;
};

const getCurrentNavElement = (
  navigationElements: (NavigationElementFieldsFragment | null)[] | null,
  currentRouteName: string
): NavigationElementFieldsFragment | undefined | null => {
  return navigationElements?.find(
    (navElement) => navElement?.id === currentRouteName || navElement?.route === currentRouteName
  );
};

const isDegreeBlockedInCurrentRegion = ({ blockedCountries = [] }: PropsFromCatalogDegreeQuery) => {
  return blockedCountries.includes(requestCountry.get());
};

class DegreePage extends React.LegacyComponentWithChildren<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof DegreePage.contextTypes>;

  state = {
    isStickyBarPresent: false,
    isStickyEoiPresent: false,
    showPersonalizedEoiModal: false,
    personalizedEoiModalStep: ACTION_TYPES.emailMeInfo,
    shouldShowTooltip: false,
    shouldShowModal: false,
    messagesToDisplay: {
      modalMessageToDisplay: null,
      embeddedMessageToDisplay: null,
      tooltipMessageToDisplay: null,
    } as MessagesToDisplay,
  };

  componentDidMount = () => {
    const { pageContent, slug, personalizedDdpData, productVariant } = this.props;

    addDriftChatWidget();

    if (pageContent?.programName) {
      setLastSeenDdp({
        slug,
        name: pageContent.programName,
      });
    }

    this.saveRecentlyViewed(pageContent, productVariant);

    window.addEventListener('scroll', this.throttledScroll);

    this.setState(() => ({
      messagesToDisplay: getMessagesToDisplay(
        personalizedDdpData,
        slug,
        pageContent?.programName,
        pageContent?.partnerInformation?.partnerName
      ),
    }));
  };

  componentDidUpdate(prevProps: RouterProps, prevState: State) {
    if (prevProps.currentRouteName !== this.props.currentRouteName) {
      window.scrollTo(0, 0);
    }

    if (!isEqual(prevState.messagesToDisplay, this.state.messagesToDisplay)) {
      if (this.state.messagesToDisplay.tooltipMessageToDisplay) {
        this.showTooltip();
      } else {
        this.hideTooltip();
      }

      if (this.state.messagesToDisplay.modalMessageToDisplay) {
        this.showModal();
      } else {
        this.hideModal();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.throttledScroll);
  }

  getHeaderLowestElement = () => {
    return document.querySelector('.header-container');
  };

  handleNavFixed = () => {
    const headerLowestElem = this.getHeaderLowestElement();
    const headerContainerElem = document.querySelector('.header-container');

    if (!headerLowestElem) {
      return;
    }

    const headerContainerBottom = headerContainerElem?.getBoundingClientRect().bottom;
    const headerBottom = headerLowestElem.getBoundingClientRect().bottom;

    if (headerBottom > 0) {
      this.setState(() => ({
        isStickyBarPresent: false,
      }));
    } else {
      this.setState(() => ({
        isStickyBarPresent: true,
      }));
    }

    // handle show Sticky EOI
    if (headerContainerBottom && headerContainerBottom > 0) {
      this.setState(() => ({
        isStickyEoiPresent: false,
      }));
    } else {
      this.setState(() => ({
        isStickyEoiPresent: true,
      }));
    }
  };

  trackDegreeApplyNow = () => {
    const { id, pageContent } = this.props;

    if (!pageContent) {
      return;
    }

    const { slug, programName } = pageContent;

    adsTracker.trackDegreeApplyNow(slug, id, programName, 'Degree');
  };

  trackRequestInfo = () => {
    const { id, pageContent } = this.props;

    if (!pageContent) {
      return;
    }

    const { slug, programName } = pageContent;

    adsTracker.trackEmailMeInfo(slug, id, programName, 'Degree');
  };

  handlePersonalizedEoiModalClose = () => {
    this.setState(() => ({
      showPersonalizedEoiModal: false,
    }));
  };

  handlePersonalizedRequestInfoClick = () => {
    this.setState(() => ({
      showPersonalizedEoiModal: true,
    }));
  };

  saveRecentlyViewed = (pageContent: $TSFixMe, productVariant: string) => {
    if (!pageContent || !pageContent.partnerInformation) {
      return;
    }

    // TODO: (Cheetahs) - update to use multiple names if coauthored when product decides how
    const { slug, programName, partnerInformation } = pageContent;
    const { partnerName, partnerLogo, bannerImage } = partnerInformation;
    const id = this.props.id;
    if (programName && slug && partnerName) {
      saveRecentlyViewed({
        id,
        name: programName,
        slug,
        path: `/degrees/${slug}`,
        imageIconWithSize: partnerLogo
          ? { imageUrl: (bannerImage?.url || partnerLogo.url) as string, size: AUTOCOMPLETE_PHOTO_SIZE }
          : DegreeIcon,
        partnerName,
        partnerLogo: bannerImage?.url || partnerLogo.url || DegreeIcon.imageUrl,
        productType: productVariant,
      });
    }
  };

  showTooltip = () => {
    this.setState(() => ({
      shouldShowTooltip: true,
    }));
  };

  hideTooltip = () => {
    this.setState(() => ({
      shouldShowTooltip: false,
    }));
  };

  showModal = () => {
    this.setState(() => ({
      shouldShowModal: true,
    }));
  };

  hideModal = () => {
    this.setState(() => ({
      shouldShowModal: false,
    }));
  };

  throttledScroll = throttle(this.handleNavFixed, 500);

  shouldShowFinancialTab = () => {
    const { pageContent } = this.props;

    if (!pageContent) {
      return false;
    }

    const { navigationElementsCollection } = pageContent;

    const hasFinancialTab =
      navigationElementsCollection?.items.some(
        (navElement) => navElement?.id === 'tuition-financing' || navElement?.id === 'costos-y-becas'
      ) || false;

    return hasFinancialTab;
  };

  showFinancialTab = this.shouldShowFinancialTab();

  renderChildrenWithPageContent = () => {
    const {
      children,
      pageContent,
      admissionsRequirements,
      specializationSlug,
      currentNavElement,
      userAgent,
      listOfUpsellsLinkedtoThisDegree,
      id: productId,
      personalizedDdpData,
    } = this.props;

    const filteredUpsells = filterUpsells(listOfUpsellsLinkedtoThisDegree);

    return React.Children.map(children, (child) => {
      return React.cloneElement(child as JSX.Element, {
        pageContent,
        admissionsRequirements,
        showFinancialTab: this.showFinancialTab,
        specializationSlug,
        isIndexPage: currentNavElement && currentNavElement.isIndexPage,
        userAgent,
        listOfUpsellsLinkedtoThisDegree: filteredUpsells,
        productId,
        personalizedDdpData,
        handlePersonalizedRequestInfoClick: this.handlePersonalizedRequestInfoClick,
        isStickyBarPresent: this.state.isStickyBarPresent,
      });
    });
  };

  renderDisclaimer = (degreeDisclaimer: $TSFixMe) => {
    if (degreeDisclaimer) {
      return <Markdown className="rc-Section disclaimer-border" source={degreeDisclaimer} />;
    }

    return <p className="disclaimer rc-Section disclaimer-border">{DEFAULT_DISCLAIMER}</p>;
  };

  render() {
    const { pageContent, currentNavElement, userAgent, id, isSurveyComplete } = this.props;
    const {
      isStickyBarPresent,
      isStickyEoiPresent,
      showPersonalizedEoiModal,
      personalizedEoiModalStep,
      shouldShowTooltip,
      shouldShowModal,
      messagesToDisplay,
    } = this.state;

    const {
      slug,
      partnerInformation,
      programName,
      bannerNotification,
      programCategoryId,
      programCategory,
      degreeDisclaimer,
      expressionOfInterestContainer,
      navigationElementsCollection,
      atAGlance,
    } = pageContent;

    const modalAction = getCtaActionXddp(
      messagesToDisplay?.modalMessageToDisplay?.ctaActionType,
      expressionOfInterestContainer?.applyButtonUrl ?? expressionOfInterestContainer?.hostedApplicationUrl,
      this.handlePersonalizedRequestInfoClick
    );

    if (isDegreeBlockedInCurrentRegion(this.props)) {
      return <DegreeNotAvailable />;
    }

    const createEOICopyTest = () => {
      if (expressionOfInterestContainer) {
        const { applyButtonText, emailMeButtonText, programInterestText } = expressionOfInterestContainer;
        return {
          ...expressionOfInterestContainer,
          applyButtonText: copyTest(`contentful-xddp-eoi-applyButtonText-${slug}-en`, applyButtonText),
          emailMeButtonText: copyTest(`contentful-xddp-eoi-emailMeButtonText-${slug}-en`, emailMeButtonText),
          programInterestText: copyTest(`contentful-xddp-eoi-programInterestText-${slug}-en`, programInterestText),
        };
      }
      return expressionOfInterestContainer;
    };
    const copy = {
      bannerNotification: copyTest(`contentful-xddp-bannerNotification-${slug}-en`, bannerNotification),
      expressionOfInterestContainer: createEOICopyTest(),
    };

    return (
      <main id="main" className="rc-DegreePage" data-testid="degreePage">
        <ChiliPiperProvider programSlug={slug as string}>
          {shouldShowTooltip && (
            <Container>
              <PersonalizedTooltip slug={slug} onXddp personalizedMessage={messagesToDisplay.tooltipMessageToDisplay} />
            </Container>
          )}
          <SharedPremiumHeader
            pageType={PREMIUM_PAGE_TYPES.DDP}
            id={id}
            userAgent={userAgent}
            slug={slug as string}
            // @ts-expect-error TODO: update type to include undefined
            currentNavElement={currentNavElement}
            // @ts-expect-error TODO: Evaluate why component complains about conditional content when it didn't before HOC refactor
            partnerInformation={partnerInformation}
            // @ts-expect-error TODO: Evaluate why component complains about conditional content when it didn't before HOC refactor
            programName={programName}
            programCategory={programCategory ?? undefined}
            programCategoryId={programCategoryId ?? undefined}
            bannerNotification={copy.bannerNotification ?? undefined}
            // @ts-expect-error TODO: Evaluate why component complains about conditional content when it didn't before HOC refactor
            expressionOfInterestContainer={copy.expressionOfInterestContainer}
            atAGlance={atAGlance}
            // @ts-expect-error TODO: Evaluate why component complains about conditional content when it didn't before HOC refactor
            navigationElements={navigationElementsCollection?.items}
            onApplyNowClick={this.trackDegreeApplyNow}
            onRequestInfoClick={this.trackRequestInfo}
            isStickyBarPresent={isStickyBarPresent}
            isStickyEoiPresent={isStickyEoiPresent}
            showPersonalizedEoiModal={showPersonalizedEoiModal}
            handlePersonalizedEoiModalClose={this.handlePersonalizedEoiModalClose}
            personalizedEoiModalStep={personalizedEoiModalStep}
            isSurveyComplete={isSurveyComplete}
          />
          <ReactScrollElement name="degreeNavigationScrollerAnchor" />
          {this.renderChildrenWithPageContent()}
          <Container>
            <Grid container justifyContent="center">
              <Grid item md={10} sm={12}>
                {this.renderDisclaimer(degreeDisclaimer)}
              </Grid>
            </Grid>
          </Container>
          {shouldShowModal && (
            <PersonalizedModal
              slug={slug}
              personalizedMessage={messagesToDisplay.modalMessageToDisplay}
              ctaAction={modalAction}
              onXddp
            />
          )}
        </ChiliPiperProvider>
      </main>
    );
  }
}

const isDegreeAvailable = (degreeAvailabilityStatus: string) => {
  return (
    degreeAvailabilityStatus === AVAILABILITY_STATUS.LIVE ||
    (isSuperuser() && degreeAvailabilityStatus === AVAILABILITY_STATUS.PRE_LAUNCH)
  );
};

const isRouteAvailable = (currentRouteName?: string) => !!currentRouteName;

const shouldShowDegree404 = ({ currentRouteName, pageContent, specializationSlug }: $TSFixMe) => {
  const hasNoPageContent = !pageContent || !pageContent.slug;
  const routeDoesNotExist = !currentRouteName;

  if (routeDoesNotExist || hasNoPageContent) {
    return true;
  }

  const isS12nPage = currentRouteName === 'specialization';

  if (isS12nPage) {
    const s12nContent = getS12nContent(pageContent, specializationSlug);

    if (!s12nContent) {
      return true;
    }
  }

  return false;
};

const DegreePageWithPersonalization: React.LegacyFunctionComponentWithChildren<Props> = (props) => {
  const { personalizedDdpData } = useDegreeDescriptionPersonalizedData();
  const { id } = props;

  const [isSurveyComplete, setIsSurveyComplete] = useState(false);
  const [retrieveCompletedSurvey] = useMutation<RetrieveCompletedSurveyResponse, RetrieveCompletedSurveyVariables>(
    RETRIEVE_COMPLETED_QUALIFICATION_SURVEY
  );
  const productId = tupleToStringKey([ProductTypes.DEGREE, id]);

  useEffect(() => {
    const getIsSurveyComplete = async () => {
      const retrieveCompletedSurveyData = await retrieveCompletedSurvey({
        variables: {
          productId,
          eoiContext: { source: document.location.href, utmParameters: {} }, // do not use UTM parameters, as we fix a backend issue. See https://coursera.slack.com/archives/C04HCP3R8BD/p1713384053888479
          lookbackPeriodInMonths: EOI_SUBMISSION_LOOKBACK_PERIOD,
        },
      });
      setIsSurveyComplete(!!retrieveCompletedSurveyData?.data?.QualificationSurveySectionResponse?.isComplete);
    };

    getIsSurveyComplete();
  }, [productId, retrieveCompletedSurvey]);

  return (
    <DegreePage {...props} personalizedDdpData={personalizedDdpData} isSurveyComplete={isSurveyComplete}>
      {props.children}
    </DegreePage>
  );
};

const DegreePageWithPersonalizationProvider: React.LegacyFunctionComponentWithChildren<Props> = (props) => {
  return (
    <DegreeDescriptionPageThemeProvider>
      <DegreeDescriptionPagePersonalizationProvider>
        <DegreeDescriptionEventingContextProvider degreeId={props.id}>
          <DegreePageWithPersonalization {...props}>{props.children}</DegreePageWithPersonalization>
        </DegreeDescriptionEventingContextProvider>
      </DegreeDescriptionPagePersonalizationProvider>
    </DegreeDescriptionPageThemeProvider>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'xddp',
    },
  })),
  connectToRouter<RouterProps>((router) => {
    const {
      params: { slug, specializationSlug },
      location,
      routes,
    } = router;

    const previewParam = location.query.preview;
    const isPreview = !!previewParam && Number(previewParam) === 1;
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const currentRouteName = routes[routes.length - 1].name;

    return {
      slug,
      isPreview,
      specializationSlug,
      currentRouteName,
      locale: getCmsLocaleString(),
    };
  }),
  waitForGraphQL(getDegreeBySlug, {
    props: ({ data }: { data?: GetDegreeBySlug_Dedupe1Query }) => ({
      degreeAvailabilityStatus: data?.PremiumProductCollections?.getBySlug?.catalogAvailabilityStatus,
      blockedCountries: data?.PremiumProductCollections?.getBySlug?.blockedCountries,
      id: data?.PremiumProductCollections?.getBySlug?.id.replace(/^degree~/, 'base~'),
      productVariant: data?.PremiumProductCollections?.getBySlug?.productVariant,
    }),
    options: ({ slug, locale }: RouterProps) => ({
      variables: {
        locale,
        slug: transformSlugIfNeeded(slug),
      },
      context: {
        clientName: 'gatewayGql',
      },
      errorPolicy: 'all',
    }),
  }),
  branch(
    ({ degreeAvailabilityStatus, currentRouteName, slug }: PropsFromCatalogDegreeQuery) => {
      if (isFakeDegree(slug)) return true;
      return isDegreeAvailable(degreeAvailabilityStatus) && isRouteAvailable(currentRouteName);
    },
    compose(
      waitForGraphQL<QueryProps, GetDegreePageContentQuery, GetDegreePageContentQueryVariables, QueryOuterProps>(
        GetDegreePageContentDocument,
        {
          options: ({ slug, isPreview, locale }) => ({
            variables: {
              slug,
              locale: '',
              preview: isPreview,
            },
            context: getGrowthCmsGraphqlContext(isPreview),
            errorPolicy: 'all',
          }),
          props: (response) => ({
            ...response?.ownProps,
            pageContent: response?.data?.universityProgramPageContentCollection?.items[0] || {},
            admissionsRequirements: response?.data?.admissionsCollection?.items[0] || {},
          }),
        }
      ),
      waitForGraphQL<
        PropsToDegreeUpsellsQuery,
        GetUpsellsBySlugQuery,
        GetUpsellsBySlugQueryVariables,
        PropsFromDegreeUpsellsQuery
      >(getUpsellsBySlug, {
        options: ({ slug }) => ({
          context: {
            clientName: 'gatewayGql',
          },
          variables: { slug },
          errorPolicy: 'all',
        }),
        props: ({ data }) => ({
          listOfUpsellsLinkedtoThisDegree: data?.UpsellQueries?.getUpsellsBySlug || [],
        }),
      })
    ),

    renderComponent(Degree404)
  ),
  branch(shouldShowDegree404, renderComponent(Degree404)),
  withProps<FromWithProps, QueryOuterProps>(({ pageContent, currentRouteName }) => {
    if (!pageContent || !pageContent.navigationElementsCollection) {
      return {};
    }

    const {
      navigationElementsCollection: { items: navigationElements },
    } = pageContent;
    const currentNavElement = getCurrentNavElement(navigationElements, currentRouteName);

    return {
      currentNavElement,
    };
  }),
  connectToStores<Props, Omit<Props, 'csrfToken'>>(['ApplicationStore'], ({ ApplicationStore }) => ({
    userAgent: ApplicationStore.getUserAgent(),
  }))
)(DegreePageWithPersonalizationProvider);
